import React from 'react';
import type { TransactionItem } from '../TransaccionesPageNew';

interface TransactionDetailProps {
    isOpen: boolean;
    transaction: TransactionItem | null;
    onClose: () => void;
}

export const TransaccionDetailDrawer: React.FC<TransactionDetailProps> = ({ isOpen, transaction, onClose }) => {
    
    if (!transaction && isOpen) return null;

    const formatMoney = (val: number) => `Bs. ${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // Cálculo del pago al anfitrión (Monto total - Comisión)
    const hostPayout = transaction ? transaction.amount - transaction.commission : 0;
    
    // Fechas
    const dateObj = transaction ? new Date(transaction.createdAt) : new Date();
    const dateStr = dateObj.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = dateObj.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

    return (
        <>
            {/* Backdrop */}
            <div 
                className={`fixed inset-0 z-40 transition-opacity duration-300 bg-gray-900/30 backdrop-blur-[2px] ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`} 
                onClick={onClose}
            />

            {/* Drawer Panel */}
            <div 
                className={`fixed inset-y-0 right-0 z-50 flex flex-col w-full sm:w-[340px] bg-white shadow-2xl transition-transform duration-300 ease-in-out transform border-l border-gray-200 ${
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                <div className="flex items-center justify-between p-5 border-b shrink-0 border-gray-100 bg-white">
                    <h3 className="m-0 text-[11px] font-bold tracking-[1px] text-gray-400 uppercase">
                        Detalle de transacción
                    </h3>
                    <button 
                        onClick={onClose}
                        className="flex items-center justify-center w-8 h-8 transition-colors rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-900"
                    >
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                {transaction && (
                    <div className="flex-1 overflow-y-auto hide-scrollbar">
                        {/* Cabecera del detalle */}
                        <div className="p-5 text-center border-b border-gray-100 bg-gray-50/50">
                            <div className="mb-2 font-mono text-[13px] text-[#4f6df5]">#{transaction.transactionCode}</div>
                            <div className="mb-1.5 font-mono text-[32px] font-bold text-emerald-500">
                                {formatMoney(transaction.amount)}
                            </div>
                            <div className="flex justify-center gap-2 mb-3">
                                <span className="px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase rounded-full whitespace-nowrap bg-emerald-50 text-emerald-600 border border-emerald-100">
                                    {transaction.transactionStatus}
                                </span>
                            </div>
                            <div className="text-xs text-gray-500">{dateStr} · {timeStr}</div>
                        </div>

                        {/* Partes involucradas */}
                        <div className="px-5 py-4 border-b border-gray-100">
                            <div className="mb-3 text-[10px] font-bold tracking-[0.8px] text-gray-400 uppercase">Partes involucradas</div>
                            <div className="flex justify-between items-center py-1.5">
                                <span className="text-xs text-gray-500">Huésped</span>
                                <div className="flex items-center gap-2">
                                    {transaction.guest.avatarColor ? (
                                        <img src={transaction.guest.avatarColor} alt="" className="w-5 h-5 rounded-full object-cover bg-gray-200" />
                                    ) : null}
                                    <span className="text-xs font-semibold text-gray-900">{transaction.guest.fullName}</span>
                                </div>
                            </div>
                            <div className="flex justify-between py-1.5 mt-1">
                                <span className="text-xs text-gray-500">Propiedad</span>
                                <span className="text-xs font-semibold text-gray-900 text-right max-w-[180px]">{transaction.propertyName}</span>
                            </div>
                        </div>

                        {/* Desglose */}
                        <div className="px-5 py-4 border-b border-gray-100">
                            <div className="mb-3 text-[10px] font-bold tracking-[0.8px] text-gray-400 uppercase">Desglose de pago</div>
                            
                            <div className="flex justify-between items-center py-1.5 border-b border-dashed border-gray-200">
                                <span className="text-xs text-gray-500">Pago a anfitrión</span>
                                <span className="font-mono text-xs text-gray-900">{formatMoney(hostPayout)}</span>
                            </div>
                            <div className="flex justify-between items-center py-1.5 border-b border-dashed border-gray-200">
                                <span className="text-xs text-gray-500">Comisión JOM</span>
                                <span className="font-mono text-xs text-amber-500">{formatMoney(transaction.commission)}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 mt-1 font-bold">
                                <span className="text-xs text-gray-900">Total cobrado</span>
                                <span className="font-mono text-[14px] text-emerald-600">{formatMoney(transaction.amount)}</span>
                            </div>
                        </div>

                        {/* Método de pago */}
                        <div className="px-5 py-4 border-b border-gray-100">
                            <div className="mb-3 text-[10px] font-bold tracking-[0.8px] text-gray-400 uppercase">Método de pago</div>
                            <div className="flex justify-between py-1.5">
                                <span className="text-xs text-gray-500">Método</span>
                                {/* <span className="font-mono text-xs font-semibold text-gray-900">{transaction.payoutTransactionId}</span> */}
                                <span className="font-mono text-xs font-semibold text-gray-900">Tarjeta</span>
                            </div>
                            {/* <div className="flex justify-between py-1.5">
                                <span className="text-xs text-gray-500">ID Propiedad</span>
                                <span className="font-mono text-[11px] font-semibold text-gray-900">Listing #{transaction.listingRentId}</span>
                            </div> */}
                        </div>

                        {/* Historial */}
                        <div className="px-5 py-4 border-b border-gray-100">
                            <div className="mb-3 text-[10px] font-bold tracking-[0.8px] text-gray-400 uppercase">Historial</div>
                            <div className="flex flex-col">
                                <div className="relative flex gap-3 py-2">
                                    <div className="flex items-center justify-center shrink-0 w-[22px] h-[22px] mt-0.5 rounded-full bg-emerald-50 border border-emerald-100">
                                        <svg width="10" height="10" fill="none" stroke="#10b981" strokeWidth="2" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                                    </div>
                                    <div className="flex-1">
                                        <p className="m-0 text-xs font-medium text-gray-900">Transacción registrada</p>
                                        <span className="text-[10px] text-gray-500">{dateStr} · {timeStr}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Botones de acción */}
                        <div className="flex flex-col gap-2 p-5 shrink-0 mt-auto">
                            <button className="w-full py-2.5 text-xs font-semibold transition-colors bg-white border rounded-lg text-gray-700 border-gray-300 hover:bg-gray-50 shadow-sm">
                                Descargar comprobante
                            </button>
                            <button className="w-full py-2.5 text-xs font-semibold transition-colors bg-white border rounded-lg text-red-600 border-red-200 hover:bg-red-50 shadow-sm">
                                Iniciar reembolso
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default TransaccionDetailDrawer;